import { REHYDRATE } from "redux-persist";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { UserData } from "../../services/auth.service.ts";
import { STORAGE_KEYS } from "../../constants/api";

interface AuthState {
  lang: string;
  loading: boolean;
  error: string | null;
  user: UserData | null;
  prevUrl: string | null;
  isAuthenticated: boolean;
  registerSuccess: boolean;
  accessToken: string | null;
  verifyEmailSuccess: boolean;
  resetPasswordSuccess: boolean;
  forgotPasswordSuccess: boolean;
}

interface PersistedState {
  auth: AuthState;
}

interface RehydrateAction {
  type: typeof REHYDRATE;
  payload: PersistedState;
}

const initialState: AuthState = {
  lang: "en",
  user: null,
  error: null,
  prevUrl: null,
  loading: false,
  accessToken: null,
  isAuthenticated: false,
  registerSuccess: false,
  verifyEmailSuccess: false,
  resetPasswordSuccess: false,
  forgotPasswordSuccess: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (
      state,
      action: PayloadAction<{
        user: UserData;
        accessToken: string;
      }>
    ) => {
      state.error = null;
      state.loading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.accessToken = action.payload.accessToken;
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    registerStart: (state) => {
      state.loading = true;
      state.error = null;
      state.registerSuccess = false;
    },
    registerSuccess: (state) => {
      state.loading = false;
      state.error = null;
      state.registerSuccess = true;
    },
    registerFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.registerSuccess = false;
    },
    verifyEmailStart: (state) => {
      state.loading = true;
      state.error = null;
      state.verifyEmailSuccess = false;
    },
    verifyEmailSuccess: (state) => {
      state.loading = false;
      state.error = null;
      state.verifyEmailSuccess = true;
    },
    verifyEmailFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.verifyEmailSuccess = false;
    },
    forgotPasswordStart: (state) => {
      state.loading = true;
      state.error = null;
      state.forgotPasswordSuccess = false;
    },
    forgotPasswordSuccess: (state) => {
      state.loading = false;
      state.error = null;
      state.forgotPasswordSuccess = true;
    },
    forgotPasswordFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.forgotPasswordSuccess = false;
    },
    resetPasswordStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    resetPasswordSuccess: (state) => {
      state.loading = false;
      state.resetPasswordSuccess = true;
    },
    resetPasswordFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.resetPasswordSuccess = false;
    },
    logout: (state) => {
      state.user = null;
      state.error = null;
      state.loading = false;
      state.accessToken = null;
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      state.isAuthenticated = false;
      state.registerSuccess = false;
      state.verifyEmailSuccess = false;
      state.resetPasswordSuccess = false;
      state.forgotPasswordSuccess = false;
    },
    refreshTokenSuccess: (
      state,
      action: PayloadAction<{ accessToken: string }>
    ) => {
      state.accessToken = action.payload.accessToken;
      localStorage.setItem(
        STORAGE_KEYS.ACCESS_TOKEN,
        action.payload.accessToken
      );
      state.isAuthenticated = true;
      state.loading = false;
      state.error = null;
    },
    setUser: (state, action: PayloadAction<UserData>) => {
      state.user = action.payload;
    },
    setLang: (state, action: PayloadAction<string>) => {
      state.lang = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resendOtpStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    resendOtpSuccess: (state) => {
      state.loading = false;
    },
    resendOtpFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    setPrevUrl: (state, action: PayloadAction<string>) => {
      state.prevUrl = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state, action: RehydrateAction) => {
      if (action.payload?.auth) {
        const persistedAuth = action.payload.auth;
        return {
          ...state,
          ...persistedAuth,
          loading: false,
          error: null,
          // Derive isAuthenticated from accessToken presence to prevent stale auth state
          isAuthenticated: !!persistedAuth.accessToken,
        };
      }
      return state;
    });
  },
});

export const {
  logout,
  setUser,
  setLang,
  clearError,
  setPrevUrl,
  loginStart,
  loginSuccess,
  loginFailure,
  registerStart,
  resendOtpStart,
  registerSuccess,
  registerFailure,
  resendOtpSuccess,
  resendOtpFailure,
  verifyEmailStart,
  verifyEmailSuccess,
  verifyEmailFailure,
  resetPasswordStart,
  forgotPasswordStart,
  refreshTokenSuccess,
  resetPasswordSuccess,
  resetPasswordFailure,
  forgotPasswordSuccess,
  forgotPasswordFailure,
} = authSlice.actions;

export default authSlice.reducer;
